import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FileModel, FilePreviewTypes, ItemsToBeRemovedOptions } from '@ca/core/models';
import { ExperimentalFeaturesService, FileDownloadService, TrackingService, UserService } from '@ca/core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as FileUtils from '../../../../utils/file/file-utils';
import { MED_UNSUPPORTED_FILE_PREVIEW_TYPES } from '../../constants/constants';
import { ShareViewDialogComponent } from '../share-view-dialog/share-view-dialog.component';

@Component({
    selector: 'ca-file-item-submenu',
    templateUrl: './file-item-submenu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [FileDownloadService],
})
export class FileItemSubmenuComponent implements OnInit {
    @Input() isPreviewBtnEnabled = false;
    @Input() isRenameBtnEnabled = false;
    @Input() isDownloadBtnEnabled = false;
    @Input() isShareBtnEnabled = false;
    @Input() isDeleteBtnEnabled = false;
    @Input() isRemoveBtnEnabled = false;
    @Input() isPredictionFile: boolean = false;
    @Input() isDownloadAnnotationsTrackingEventEnabled: boolean = false;
    @Input() datasetId: string = '';

    @Input() shareUrl: string;
    @Input() fileModel: FileModel;
    @Input() result?;
    @Input() trackedPredictionFiles?: FileModel[];
    @Output() openChange = new EventEmitter<boolean>();
    @Output() previewFile = new EventEmitter<FileModel>();
    @Output() deleteItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel>>();
    @Output() removeItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel>>();

    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    constructor(
        private modalService: NgbModal,
        private fileDownloadService: FileDownloadService,
        private mixpanelTrackingService: TrackingService,
        private userService: UserService,
        public experimentalFeaturesService: ExperimentalFeaturesService,
    ) {}

    /**
     * Lifecycle hook called on component initialization.
     * Disables preview if the file type is unsupported for MED projects.
     */
    ngOnInit(): void {
        if (this.isFileTypePreviewUnsupportedOnMED()) {
            this.isPreviewBtnEnabled = false;
        }
    }

    /**
     * Emits an event to preview the file.
     */
    onPreview(): void {
        this.previewFile.emit(this.fileModel);
    }

    /**
     * Handles the download process for the file and tracks related events.
     */
    onDownload(): void {
        if (this.isDownloadAnnotationsTrackingEventEnabled) {
            this.mixpanelTrackingService.trackEvent('download_annotation_started', {
                userId: this.userService.myInfo?.sub,
                datasetId: this.datasetId,
            });
        } else if (this.isPredictionFile) {
            this.mixpanelTrackingService.trackEvent('download_prediction_started', {
                userId: this.userService.myInfo?.sub,
                modelId: this.result?.workflowRunId,
                modelVersionId: this.result?.azureMlModelVersionId,
                fileIds: this.trackedPredictionFiles,
                modelType: this.result?.trainingType,
            });
        }
        const { token, name, id } = this.fileModel;
        this.fileDownloadService.nativeDownload(token, name, id);
    }

    /**
     * Emits an event to delete the file.
     */
    onDelete(): void {
        this.deleteItems.emit({ items: [this.fileModel], shouldConfirmDialogBeOpened: true });
    }

    /**
     * Emits an event to remove the file.
     */
    onRemove(): void {
        this.removeItems.emit({ items: [this.fileModel], shouldConfirmDialogBeOpened: true });
    }

    /**
     * Opens the share view dialog for the file.
     */
    openShareViewDialog(): void {
        const modal = this.modalService.open(ShareViewDialogComponent, { size: 'lg', centered: true });
        modal.componentInstance.viewerLink = this.shareUrl;
    }

    /**
     * Checks if the file type is unsupported for preview in MED projects.
     * @returns `true` if the file type is unsupported, otherwise `false`.
     */
    private isFileTypePreviewUnsupportedOnMED(): boolean {
        const fileExtension = FileUtils.getFileExtension(this.fileModel?.name);
        return this.isMEDProjectEnabled && MED_UNSUPPORTED_FILE_PREVIEW_TYPES.includes(fileExtension as FilePreviewTypes);
    }
}
